import React, { useState, useMemo, useEffect } from 'react';
import { useArticles } from '../../api/ArticleContext2';
import { Link, useNavigate } from 'react-router-dom';
import {
  Card, Typography, Tag, Row, Col, Statistic, Button, Space, Divider,
  Avatar, Badge, Empty, List, Grid, Input, Alert, Spin, Pagination
} from 'antd';
import {
  EditOutlined, FileTextOutlined, ClockCircleOutlined,
  UserOutlined, FireOutlined, BookOutlined, CalendarOutlined,
  SearchOutlined, EyeOutlined, ClearOutlined, TagOutlined, 
  HomeOutlined, RocketOutlined, CodeOutlined, StarOutlined,
  ThunderboltOutlined, CrownOutlined, CloudOutlined, GlobalOutlined
} from '@ant-design/icons';
import './Home.css';

const { Title, Paragraph, Text } = Typography;
const { Search } = Input;
const { useBreakpoint } = Grid;

// 蓝色主题的渐变背景数组
const blueGradients = [
  'linear-gradient(135deg, #1890ff 0%, #36cfc9 100%)',
  'linear-gradient(135deg, #096dd9 0%, #1890ff 100%)',
  'linear-gradient(135deg, #0050b3 0%, #096dd9 100%)',
  'linear-gradient(135deg, #40a9ff 0%, #69c0ff 100%)',
  'linear-gradient(135deg, #0050b3 0%, #1890ff 50%, #40a9ff 100%)',
  'linear-gradient(135deg, #36cfc9 0%, #13c2c2 100%)',
];

// 装饰图案组件
const BlueThemePattern = ({ type = 'circles' }) => {
  if (type === 'circles') {
    return (
      <>
        <div style={{
          position: 'absolute',
          top: -30,
          right: -30,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
          zIndex: 1,
        }} />
        <div style={{
          position: 'absolute',
          bottom: -20,
          left: -20,
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          zIndex: 1,
        }} />
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
          zIndex: 1,
        }} />
      </>
    );
  }
  
  if (type === 'waves') {
    return (
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          repeating-linear-gradient(
            45deg,
            transparent,
            transparent 10px,
            rgba(255,255,255,0.05) 10px,
            rgba(255,255,255,0.05) 20px
          )
        `,
        zIndex: 1,
      }} />
    );
  }
  
  return null;
};

// 获取随机蓝色渐变
const getRandomBlueGradient = () => {
  return blueGradients[Math.floor(Math.random() * blueGradients.length)];
};

const Home = () => {
  const { getAllArticles, getPaginatedArticles, getTotalArticlesCount } = useArticles();
  const allArticles = getAllArticles();
  const totalReadTime = allArticles.reduce((sum, article) => {
    const minutes = parseInt(article.readTime) || 0;
    return sum + minutes;
  }, 0);
  const screens = useBreakpoint();
  const navigate = useNavigate();

  const [searchKeyword, setSearchKeyword] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [featuredGradient, setFeaturedGradient] = useState(blueGradients[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  // 搜索功能
  const handleSearch = (value) => {
    const keyword = value.trim();
    setSearchKeyword(keyword);
    setIsSearching(true);
    
    if (!keyword) {
      setSearchResults([]);
      setShowResults(false);
      setIsSearching(false);
      return;
    }

    setTimeout(() => {
      const results = allArticles.filter(article => {
        const searchText = keyword.toLowerCase();
        const inTitle = article.title.toLowerCase().includes(searchText);
        const inExcerpt = article.excerpt.toLowerCase().includes(searchText);
        const inAuthor = article.author.toLowerCase().includes(searchText);
        const inTags = article.tags.some(tag => 
          tag.toLowerCase().includes(searchText)
        );
        const inCategory = article.category.toLowerCase().includes(searchText);
        
        return inTitle || inExcerpt || inAuthor || inTags || inCategory;
      });
      
      setSearchResults(results);
      setShowResults(true);
      setCurrentPage(1);
      setIsSearching(false);
    }, 500);
  };

  // 清空搜索
  const clearSearch = () => {
    setSearchKeyword('');
    setSearchResults([]);
    setShowResults(false);
    setCurrentPage(1);
  };

  // 获取当前显示的文章
  const getDisplayArticles = () => {
    if (showResults && searchKeyword) {
      return searchResults;
    }
    return allArticles;
  };

  // 获取分页文章
  const getPaginatedDisplayArticles = () => {
    const articles = getDisplayArticles();
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return articles.slice(start, end);
  };

  // 高亮显示搜索关键词
  const highlightText = (text) => {
    if (!searchKeyword || !text) return text;
    
    const regex = new RegExp(`(${searchKeyword})`, 'gi');
    return text.toString().split(regex).map((part, index) =>
      regex.test(part) ? (
        <span key={index} style={{ 
          backgroundColor: '#fff566', 
          padding: '0 2px', 
          borderRadius: '2px',
          color: '#1f1f1f'
        }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const getAvatarColor = (name) => {
    const colors = ['#1890ff', '#722ed1', '#1890ff', '#fa8c16', '#52c41a'];
    return colors[name.charCodeAt(0) % colors.length];
  };

  const FeaturedArticle = () => {
    const displayArticles = getDisplayArticles();
    if (displayArticles.length === 0) return null;
    
    const article = displayArticles[0];
    const gradient = featuredGradient;

    // 点击整个卡片跳转到文章详情
    const handleCardClick = (e) => {
      // 如果点击的是标签或按钮，不跳转
      if (e.target.closest('.no-navigation') || e.target.tagName === 'BUTTON' || e.target.tagName === 'A') {
        return;
      }
      navigate(`/post/${article.id}`);
    };

    return (
      <Card
        hoverable
        className="home-content-card featured-article-card"
        styles={{
          body: {
            padding: 24
          }
        }}
        style={{ 
          marginBottom: 24,
          border: 'none',
          overflow: 'hidden',
          cursor: 'pointer'
        }}
        onClick={handleCardClick}
        cover={
          screens.lg ? (
            <div style={{ 
              height: 200, 
              background: gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* 装饰图案 */}
              <BlueThemePattern type="circles" />
              
              {/* 动态网格背景 */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
                `,
                backgroundSize: '20px 20px',
                opacity: 0.3,
              }} />
              
              {/* 主图标和标题 */}
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
                textAlign: 'center',
                padding: '0 20px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 16
                }}>
                  <div style={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 16
                  }}>
                    <BookOutlined style={{ fontSize: 32, color: 'white' }} />
                  </div>
                  <GlobalOutlined style={{ fontSize: 32, color: 'white', opacity: 0.8 }} />
                </div>
                
                <div style={{
                  fontSize: 28,
                  fontWeight: 600,
                  marginBottom: 8,
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  {highlightText(article.title)}
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 14,
                  opacity: 0.9
                }}>
                  <Space>
                    <CalendarOutlined />
                    <span>{article.date}</span>
                  </Space>
                  <Space>
                    <ClockCircleOutlined />
                    <span>{article.readTime} 分钟阅读</span>
                  </Space>
                </div>
              </div>
              
              {/* 装饰性元素 */}
              <div style={{
                position: 'absolute',
                top: 20,
                left: 20,
                background: 'rgba(255, 255, 255, 0.2)',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: 12,
                backdropFilter: 'blur(4px)'
              }}>
                <CrownOutlined /> 精选文章
              </div>
              
              <div style={{
                position: 'absolute',
                bottom: 20,
                right: 20,
                display: 'flex',
                gap: 8
              }}>
                {article.tags.slice(0, 2).map(tag => (
                  <div 
                    key={tag} 
                    className="no-navigation"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchKeyword(tag);
                      handleSearch(tag);
                    }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: 11,
                      backdropFilter: 'blur(4px)',
                      cursor: 'pointer'
                    }}
                  >
                    {tag}
                  </div>
                ))}
              </div>
            </div>
          ) : null
        }
      >
        {/* 移动端标题显示 */}
        {!screens.lg && (
          <div style={{ marginBottom: 16 }}>
            {!showResults ? (
              <Badge.Ribbon text="精选" color="#1890ff">
                <Title level={3} style={{ margin: 0 }}>
                  {highlightText(article.title)}
                </Title>
              </Badge.Ribbon>
            ) : (
              <Title level={3} style={{ margin: 0 }}>
                {highlightText(article.title)}
              </Title>
            )}
          </div>
        )}
        
        <Paragraph 
          style={{ 
            color: '#4e5969', 
            lineHeight: 1.6, 
            marginBottom: 24,
            fontSize: '15px'
          }}
          ellipsis={{ rows: 3 }}
        >
          {highlightText(article.excerpt)}
        </Paragraph>
        
        <Divider style={{ margin: '16px 0' }} />
        
        <Row justify="space-between" align="middle">
          <Space split={<Divider type="vertical" />}>
            <Space 
              className="no-navigation"
              onClick={(e) => {
                e.stopPropagation();
                setSearchKeyword(article.author);
                handleSearch(article.author);
              }}
              style={{ cursor: 'pointer' }}
            >
              <Avatar 
                size="small"
                style={{ 
                  backgroundColor: getAvatarColor(article.author),
                  boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
                }}
              >
                {article.author.charAt(0)}
              </Avatar>
              <Text type="secondary" strong>{highlightText(article.author)}</Text>
            </Space>
            <Space>
              <CalendarOutlined />
              <Text type="secondary">{article.date}</Text>
            </Space>
            <Space>
              <ClockCircleOutlined />
              <Text type="secondary">{article.readTime} 分钟阅读</Text>
            </Space>
          </Space>
          
          <Space wrap>
            {article.tags.slice(0, 3).map(tag => (
              <Tag 
                key={tag} 
                color="blue" 
                style={{ 
                  borderRadius: '12px',
                  padding: '1px 10px',
                  margin: 0
                }}
                className="no-navigation"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchKeyword(tag);
                  handleSearch(tag);
                }}
              >
                {highlightText(tag)}
              </Tag>
            ))}
          </Space>
        </Row>
      </Card>
    );
  };

  // 监听文章变化，随机选择渐变
  useEffect(() => {
    if (allArticles.length > 0) {
      setFeaturedGradient(getRandomBlueGradient());
    }
  }, [allArticles]);

  const displayArticles = getDisplayArticles();
  const paginatedArticles = getPaginatedDisplayArticles();
  const totalArticles = displayArticles.length;
  const totalPages = Math.ceil(totalArticles / pageSize);

  return (
    <div className="home-container">
      {/* 头部搜索区域 */}
      <div className="home-header" style={{
        background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)',
        borderRadius: '12px',
        margin: '0 24px 32px',
        border: '1px solid #d6e4ff'
      }}>
        <div style={{ 
          position: 'relative',
          maxWidth: '800px',
          margin: '0 auto',
          padding: '40px 20px'
        }}>
          <Title level={2} style={{ 
            marginBottom: 8,
            color: '#1890ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'rgba(24, 144, 255, 0.1)',
              marginRight: 16
            }}>
              <CodeOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            </div>
            技术博客
          </Title>
          
          <Paragraph type="secondary" style={{ 
            marginBottom: 32,
            fontSize: '16px',
            color: '#666',
            textAlign: 'center'
          }}>
            <ThunderboltOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            深耕前端技术 · 分享实战心得 · 记录成长轨迹
          </Paragraph>
          
          <div style={{ position: 'relative' }}>
            <Search
              placeholder="搜索文章、标签或作者..."
              enterButton={
                <Button 
                  type="primary" 
                  icon={<SearchOutlined />}
                  style={{ 
                    background: 'linear-gradient(135deg, #1890ff 0%, #36cfc9 100%)',
                    border: 'none'
                  }}
                >
                  搜索
                </Button>
              }
              size="large"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onSearch={handleSearch}
              loading={isSearching}
              style={{ 
                maxWidth: '600px',
                margin: '0 auto',
              }}
              allowClear
            />
            
            {/* 装饰性背景 */}
            <div style={{
              position: 'absolute',
              top: -20,
              left: '10%',
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(24, 144, 255, 0.1) 0%, rgba(54, 207, 201, 0.1) 100%)',
              zIndex: 0,
            }} />
            <div style={{
              position: 'absolute',
              bottom: -10,
              right: '10%',
              width: 30,
              height: 30,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(24, 144, 255, 0.1) 0%, rgba(54, 207, 201, 0.1) 100%)',
              zIndex: 0,
            }} />
          </div>
          
          {showResults && searchKeyword && (
            <div style={{ marginTop: 16 }}>
              <Alert
                message={
                  <Space>
                    <SearchOutlined />
                    <span>
                      搜索 "<Text strong>{searchKeyword}</Text>" 的结果：
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        找到 {searchResults.length} 篇文章
                      </Text>
                    </span>
                    <Button 
                      size="small" 
                      type="text" 
                      icon={<ClearOutlined />} 
                      onClick={clearSearch}
                      style={{ marginLeft: 8 }}
                    >
                      清除搜索
                    </Button>
                  </Space>
                }
                type="info"
                showIcon={false}
                style={{ 
                  background: 'rgba(24, 144, 255, 0.1)', 
                  borderColor: '#91d5ff',
                  borderRadius: '8px'
                }}
              />
            </div>
          )}
        </div>
      </div>

      <Row gutter={[24, 24]} style={{ padding: '0 24px' }}>
        {/* 左侧边栏 */}
        <Col xs={24} md={8} lg={6}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {/* 数据统计 */}
            <Card 
              bordered={false} 
              className="home-filter-card"
              styles={{
                body: {
                  padding: 20
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f9fcff 100%)',
                border: '1px solid #e6f7ff',
                borderRadius: '12px'
              }}
            >
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic 
                    title="文章总数" 
                    value={allArticles.length}
                    prefix={<FileTextOutlined style={{ color: '#1890ff' }} />}
                    valueStyle={{ fontSize: '20px', color: '#1890ff' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="阅读分钟" 
                    value={totalReadTime}
                    prefix={<ClockCircleOutlined style={{ color: '#36cfc9' }} />}
                    valueStyle={{ fontSize: '20px', color: '#36cfc9' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="建站年份" 
                    value={2023}
                    prefix={<CalendarOutlined style={{ color: '#722ed1' }} />}
                    valueStyle={{ fontSize: '20px', color: '#722ed1' }}
                  />
                </Col>
              </Row>
            </Card>

            {/* 热门标签 */}
            <Card 
              size="small" 
              title={
                <Space>
                  <TagOutlined style={{ color: '#1890ff' }} />
                  <span>热门标签</span>
                </Space>
              }
              bordered={false}
              className="home-filter-card"
              styles={{
                body: {
                  padding: 20
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f9fcff 100%)',
                border: '1px solid #e6f7ff',
                borderRadius: '12px'
              }}
            >
              <Space wrap>
                {(() => {
                  const tagCounts = {};
                  allArticles.forEach(article => {
                    article.tags.forEach(tag => {
                      tagCounts[tag] = (tagCounts[tag] || 0) + 1;
                    });
                  });
                  
                  const popularTags = Object.entries(tagCounts)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 8)
                    .map(([tag]) => tag);
                  
                  return popularTags.map(tag => (
                    <Tag 
                      key={tag} 
                      color="blue"
                      style={{ 
                        cursor: 'pointer', 
                        marginBottom: 4,
                        borderRadius: '12px',
                        padding: '1px 10px',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => {
                        setSearchKeyword(tag);
                        handleSearch(tag);
                      }}
                    >
                      {tag}
                    </Tag>
                  ));
                })()}
              </Space>
            </Card>

            {/* 快捷操作 */}
            <Card 
              size="small" 
              title={
                <Space>
                  <RocketOutlined style={{ color: '#1890ff' }} />
                  <span>快捷操作</span>
                </Space>
              }
              bordered={false}
              className="home-filter-card"
              styles={{
                body: {
                  padding: 20
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f9fcff 100%)',
                border: '1px solid #e6f7ff',
                borderRadius: '12px'
              }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  block 
                  icon={<EditOutlined />}
                  component={Link}
                  to="/write"
                  style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #36cfc9 100%)',
                    border: 'none',
                    height: '40px',
                    borderRadius: '8px'
                  }}
                >
                  开始写作
                </Button>
                <Button 
                  block 
                  icon={<FileTextOutlined />}
                  component={Link}
                  to="/archive"
                  style={{
                    height: '40px',
                    borderRadius: '8px',
                    borderColor: '#d6e4ff'
                  }}
                >
                  文章归档
                </Button>
                <Button 
                  block 
                  icon={<UserOutlined />}
                  component={Link}
                  to="/about"
                  style={{
                    height: '40px',
                    borderRadius: '8px',
                    borderColor: '#d6e4ff'
                  }}
                >
                  关于作者
                </Button>
                {showResults && (
                  <Button 
                    block 
                    icon={<HomeOutlined />}
                    onClick={clearSearch}
                    style={{
                      height: '40px',
                      borderRadius: '8px',
                      borderColor: '#d6e4ff'
                    }}
                  >
                    返回全部文章
                  </Button>
                )}
              </Space>
            </Card>
          </Space>
        </Col>

        {/* 主内容区 */}
        <Col xs={24} md={16} lg={18}>
          {/* 精选文章或搜索结果 */}
          {showResults ? (
            searchResults.length > 0 ? (
              <>
                <FeaturedArticle />
                <Card 
                  bordered={false}
                  className="home-content-card"
                  title={
                    <Space>
                      <SearchOutlined style={{ color: '#1890ff' }} />
                      <span>搜索结果</span>
                      <Text type="secondary" style={{ fontSize: 14 }}>
                        （共 {searchResults.length} 篇）
                      </Text>
                    </Space>
                  }
                  styles={{
                    body: {
                      padding: 24
                    }
                  }}
                  style={{
                    background: '#fff',
                    borderRadius: '12px',
                    border: '1px solid #e6f7ff'
                  }}
                >
                  <List
                    itemLayout="vertical"
                    dataSource={paginatedArticles.slice(1)}
                    renderItem={(article) => (
                      <List.Item
                        key={article.id}
                        style={{ 
                          padding: '16px 0', 
                          borderBottom: '1px solid #f0f0f0',
                          transition: 'background-color 0.3s ease'
                        }}
                        className="article-list-item"
                      >
                        <List.Item.Meta
                          avatar={
                            <Avatar 
                              style={{ 
                                backgroundColor: getAvatarColor(article.author),
                                boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
                              }}
                            >
                              {article.author.charAt(0)}
                            </Avatar>
                          }
                          title={
                            <Link to={`/post/${article.id}`} style={{ fontSize: '16px' }}>
                              {highlightText(article.title)}
                            </Link>
                          }
                          description={
                            <Paragraph 
                              type="secondary"
                              style={{ marginBottom: 0 }}
                              ellipsis={{ rows: 2 }}
                            >
                              {highlightText(article.excerpt)}
                            </Paragraph>
                          }
                        />
                        <Space wrap style={{ marginTop: 8 }}>
                          {article.tags.slice(0, 3).map(tag => (
                            <Tag 
                              key={tag} 
                              size="small" 
                              color="blue"
                              icon={<TagOutlined />}
                              style={{ borderRadius: '10px' }}
                              onClick={() => {
                                setSearchKeyword(tag);
                                handleSearch(tag);
                              }}
                            >
                              {highlightText(tag)}
                            </Tag>
                          ))}
                          <Tag size="small" color="green" style={{ borderRadius: '10px' }}>
                            {article.category}
                          </Tag>
                        </Space>
                      </List.Item>
                    )}
                  />
                  
                  {totalPages > 1 && (
                    <div style={{ marginTop: 24, textAlign: 'center' }}>
                      <Pagination
                        current={currentPage}
                        total={searchResults.length}
                        pageSize={pageSize}
                        onChange={(page) => setCurrentPage(page)}
                        showSizeChanger={false}
                        showQuickJumper
                        showTotal={(total) => `共 ${total} 条`}
                      />
                    </div>
                  )}
                </Card>
              </>
            ) : (
              <Card 
                bordered={false} 
                className="home-content-card"
                styles={{
                  body: {
                    padding: 40
                  }
                }}
              >
                <Empty
                  description={
                    <div>
                      <div style={{ marginBottom: 8 }}>
                        没有找到与 "<Text strong>{searchKeyword}</Text>" 相关的文章
                      </div>
                      <Text type="secondary">
                        尝试搜索其他关键词，或者查看全部文章
                      </Text>
                    </div>
                  }
                  style={{ margin: '40px 0' }}
                >
                  <Space>
                    <Button
                      type="primary"
                      onClick={clearSearch}
                      style={{
                        background: 'linear-gradient(135deg, #1890ff 0%, #36cfc9 100%)',
                        border: 'none'
                      }}
                    >
                      查看全部文章
                    </Button>
                    <Button
                      onClick={() => {
                        setSearchKeyword('');
                        setShowResults(false);
                      }}
                    >
                      重新搜索
                    </Button>
                  </Space>
                </Empty>
              </Card>
            )
          ) : (
            <>
              {/* 精选文章 */}
              <FeaturedArticle />

              {/* 最新文章 */}
              <Card 
                bordered={false}
                className="home-content-card"
                title={
                  <Space>
                    <FireOutlined style={{ color: '#fa8c16' }} />
                    <span>最新文章</span>
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      （共 {totalArticles} 篇）
                    </Text>
                  </Space>
                }
                styles={{
                  body: {
                    padding: 24
                  }
                }}
                style={{
                  background: '#fff',
                  borderRadius: '12px',
                  border: '1px solid #e6f7ff'
                }}
              >
                {displayArticles.length === 0 ? (
                  <Empty
                    description={
                      <Paragraph style={{ color: '#86909c' }}>
                        暂无文章，开启你的第一篇创作吧！
                      </Paragraph>
                    }
                    style={{ margin: '40px 0' }}
                  >
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      component={Link}
                      to="/write"
                      style={{
                        background: 'linear-gradient(135deg, #1890ff 0%, #36cfc9 100%)',
                        border: 'none'
                      }}
                    >
                      开始写作
                    </Button>
                  </Empty>
                ) : (
                  <>
                    <List
                      itemLayout="vertical"
                      dataSource={paginatedArticles.slice(1)}
                      renderItem={(article) => (
                        <List.Item
                          key={article.id}
                          style={{ 
                            padding: '16px 0', 
                            borderBottom: '1px solid #f0f0f0',
                            transition: 'background-color 0.3s ease'
                          }}
                          className="article-list-item"
                        >
                          <List.Item.Meta
                            avatar={
                              <Avatar 
                                style={{ 
                                  backgroundColor: getAvatarColor(article.author),
                                  boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
                                }}
                              >
                                {article.author.charAt(0)}
                              </Avatar>
                            }
                            title={
                              <Link to={`/post/${article.id}`} style={{ fontSize: '16px' }}>
                                {article.title}
                              </Link>
                            }
                            description={
                              <Paragraph 
                                type="secondary"
                                style={{ marginBottom: 0 }}
                                ellipsis={{ rows: 2 }}
                              >
                                {article.excerpt}
                              </Paragraph>
                            }
                          />
                          <Space wrap style={{ marginTop: 8 }}>
                            {article.tags.slice(0, 3).map(tag => (
                              <Tag 
                                key={tag} 
                                size="small" 
                                color="blue" 
                                style={{ borderRadius: '10px' }}
                                onClick={() => {
                                  setSearchKeyword(tag);
                                  handleSearch(tag);
                                }}
                              >
                                {tag}
                              </Tag>
                            ))}
                          </Space>
                        </List.Item>
                      )}
                    />
                    
                    {totalPages > 1 && (
                      <div style={{ marginTop: 24, textAlign: 'center' }}>
                        <Pagination
                          current={currentPage}
                          total={totalArticles}
                          pageSize={pageSize}
                          onChange={(page) => setCurrentPage(page)}
                          showSizeChanger={false}
                          showQuickJumper
                          showTotal={(total) => `共 ${total} 条`}
                        />
                      </div>
                    )}
                  </>
                )}
              </Card>
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Home;